import { z } from "zod";
import { RichText } from "../RichText";
import { globalSectionSchema, backgroundStyleEnum } from "../globalProps";
import { SectionShell } from "../SectionShell";
import { validateMapUrl } from "../embedUtils";

export const schema = globalSectionSchema.extend({
  heading: z.string().default("Visit Our Office"),
  address: z.string().default("123 Main Street, Suite 200, Anytown, ST 10001"),
  embedUrl: z.string().optional(),
  height: z.number().int().min(200).default(400),
  showAddress: z.boolean().default(true),
  backgroundStyle: backgroundStyleEnum.default("light"),
});

export type Props = z.infer<typeof schema>;
export const defaults: Props = schema.parse({});

export function Component(props: Props) {
  const result = schema.safeParse(props);
  const safe = result.success ? result.data : defaults;

  const addressFallback = `https://maps.google.com/maps?q=${encodeURIComponent(safe.address)}&output=embed`;
  const mapSrc =
    safe.embedUrl && validateMapUrl(safe.embedUrl)
      ? safe.embedUrl
      : addressFallback;

  const mapIframe = (
    <div className="rounded-lg overflow-hidden bg-surface" style={{ height: safe.height }}>
      <iframe
        src={mapSrc}
        className="w-full h-full border-0"
        sandbox="allow-scripts allow-same-origin"
        loading="lazy"
        title={safe.heading || "Map"}
        referrerPolicy="no-referrer-when-downgrade"
      />
    </div>
  );

  // Render address lines (split on newlines)
  const addressLines = safe.address.split("\n");

  if (safe.showAddress) {
    return (
      <SectionShell globals={safe}>
          <div className="grid grid-cols-1 @md:grid-cols-2 gap-10 @md:gap-16 items-start">
            {/* Address side */}
            <div>
              <RichText
                as={safe.headingLevel}
                html={safe.heading}
                className="text-3xl @md:text-[2.5rem] font-bold mb-6"
                data-prop="heading"
              />
              <address className="not-italic text-lg leading-relaxed opacity-80">
                {addressLines.map((line, i) => (
                  <span key={i}>
                    {i > 0 && <br />}
                    {line}
                  </span>
                ))}
              </address>
            </div>

            {/* Map side */}
            {mapIframe}
          </div>
      </SectionShell>
    );
  }

  // Full-width map, optional heading above
  return (
    <SectionShell globals={safe}>
        {safe.heading && (
          <RichText
            as={safe.headingLevel}
            html={safe.heading}
            className="text-3xl @md:text-[2.5rem] font-bold mb-8 text-center"
            data-prop="heading"
          />
        )}
        {mapIframe}
    </SectionShell>
  );
}
